<template>
  <div
    class="min-h-screen flex flex-col justify-center items-center xl:mx-10 2xl:mx-16"
  >
    <div
      class="rounded-xl border-opacity-0 md:border-opacity-100 md:border md:border-gray-200 bg-white py-10 md:py-16 px-5 md:px-16 lg:px-24 w-full"
    >
      <div class="text-center mb-5 md:mb-16">
        <p class="text-3xl font-bold mb-2">{{ t('importTitle') }}</p>
        <p class="text-lg">
          {{ t('importSubtitle', { name: verifiableCredential.name }) }}
        </p>
      </div>
      <div
        class="flex flex-col md:flex-row items-center justify-center gap-10 mb-10"
      >
        <div class="py-3 px-5 md:my-2 rounded-lg">
          <TemplatePreview
            :templateImage="verifiableCredential.image"
            :templateName="verifiableCredential.name"
            :templateLocation="
              verifiableCredential.type === 'Participation'
                ? verifiableCredential?.location?.split(', ')[0] || 'Virtual'
                : null
            "
            :templateStartDate="
              verifiableCredential.type === 'Participation'
                ? verifiableCredential.startDate
                : null
            "
            :templateEndDate="
              verifiableCredential.type === 'Participation'
                ? verifiableCredential.endDate
                : null
            "
            :templateThemeBgColor="
              verifiableCredential.badgeTheme
                ? verifiableCredential.badgeTheme.backgroundCardColor
                : null
            "
            :templateThemeTextColor="
              verifiableCredential.badgeTheme
                ? verifiableCredential.badgeTheme.textColor
                : null
            "
            :templateThemeEffect="
              verifiableCredential.badgeTheme
                ? verifiableCredential.badgeTheme.cardEffect
                : null
            "
            :templateThemeTexture="
              verifiableCredential.badgeTheme
                ? verifiableCredential.badgeTheme.textureImageURL
                : null
            "
            :templateThemeIsLogoBack="
              verifiableCredential.badgeTheme
                ? verifiableCredential.badgeTheme.isLogoBack
                : null
            "
            :templateThemeShape="
              verifiableCredential.badgeTheme
                ? verifiableCredential.badgeTheme.shape
                : null
            "
            :issuerName="
              verifiableCredential.type === 'Community'
                ? verifiableCredential.templateIssuer.name
                : verifiableCredential.issuer.name
            "
            :issuerImage="
              verifiableCredential.type === 'Community'
                ? verifiableCredential.templateIssuer.profileImage
                : verifiableCredential.issuer.profileImage
            "
            :formatSm="false"
            :type="verifiableCredential.type"
            :is3d="true"
          />
        </div>

        <div
          v-if="showQR && verifiableCredential.dynamicLink"
          class="flex flex-col items-center"
        >
          <div class="border border-gray-100 rounded-lg shadow-lg p-3 mb-5">
            <qrcode-vue
              :value="verifiableCredential.dynamicLink"
              :size="160"
              level="L"
              :foreground="'#111827'"
              margin="2"
            />
          </div>
          <div class="text-sm mt-2">{{ t('scanQR') }}</div>
        </div>
        <div v-else class="flex justify-center items-center py-2">
          <a
            v-if="verifiableCredential.dynamicLink"
            :href="verifiableCredential.dynamicLink"
            class="bg-[#4F46E5] text-white rounded-lg flex justify-center items-center gap-2 px-10 py-3 text-base font-medium"
            target="_blank"
          >
            <icon name="download" class="fill-white w-7" />
            {{ t('getBadge') }}
          </a>
        </div>
      </div>
      <div class="w-full grid grid-cols-1 md:grid-cols-3 gap-5">
        <div class="text-center px-10 md:px-0">
          <div
            class="flex w-20 h-20 items-center justify-center bg-blue-200 rounded-full border-4 border-white shadow-md mx-auto mb-3"
          >
            <icon name="qr-code" class="fill-blue-500 w-1/2 h-1/2" />
          </div>
          <div class="text-lg font-bold mb-3">{{ t('step1Title') }}</div>
          <div class="text-sm font-bold text-gray-500">
            {{ t('step1Desc') }}
          </div>
        </div>
        <div class="text-center">
          <div
            class="flex w-20 h-20 items-center justify-center bg-blue-200 rounded-full border-4 border-white shadow-md mx-auto mb-3"
          >
            <icon name="finger" class="fill-blue-500 w-1/2 h-1/2 p-0.5" />
          </div>
          <div class="text-lg font-bold mb-3">{{ t('step2Title') }}</div>
          <div class="text-sm font-bold text-gray-500">
            {{ t('step2Desc') }}
          </div>
        </div>
        <div class="text-center">
          <div
            class="flex w-20 h-20 items-center justify-center bg-green-200 rounded-full border-4 border-white shadow-md mx-auto mb-3"
          >
            <icon name="badge" class="fill-green-500 w-1/2 h-1/2 p-1" />
          </div>
          <div class="text-lg font-bold mb-3">{{ t('step3Title') }}</div>
          <div class="text-sm font-bold text-gray-500">
            {{ t('step3Desc') }}
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-col mt-10 w-full">
      <the-footer></the-footer>
    </div>
  </div>
</template>

<script>
import { ref, onUnmounted } from 'vue';
import TemplatePreview from './TemplatePreview.vue';
import TemplateRolePreview from './TemplateRolePreview.vue';
import { t } from '../locales/index.js';

export default {
  props: {
    verifiableCredential: Object,
  },
  setup() {
    const isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );

    const showQR = ref(!isMobile && window.innerWidth >= 1024);

    function updateShowQR() {
      showQR.value = !isMobile && window.innerWidth >= 1024;
    }

    window.addEventListener('resize', updateShowQR);
    onUnmounted(() => window.removeEventListener('resize', updateShowQR));

    return { showQR, t };
  },

  components: {
    TemplatePreview,
    TemplateRolePreview,
  },
};
</script>
